import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Users, Car, Clock, ArrowRight, Plus } from 'lucide-react';
import { useAuthStore } from '../../store/auth';
import { api } from '../../lib/api';
import { formatDate } from '../../lib/utils';

interface TeamSummary {
  id: string;
  name: string;
  slug: string;
  role: string;
  memberCount: number;
}

interface BookingSummary {
  id: string;
  trackName: string;
  bookingDate: string;
  startTime: string;
  endTime: string;
  status: string;
  teamName: string;
}

export function DashboardHome() {
  const { user } = useAuthStore();
  const [teams, setTeams] = useState<TeamSummary[]>([]);
  const [upcomingBookings, setUpcomingBookings] = useState<BookingSummary[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchData() {
      try {
        const [teamsRes, bookingsRes] = await Promise.all([
          api.get('/api/teams'),
          api.get('/api/bookings?status=approved&upcoming=true&limit=5'),
        ]);

        if (teamsRes.teams) {
          setTeams(teamsRes.teams);
        }
        if (bookingsRes.bookings) {
          setUpcomingBookings(bookingsRes.bookings);
        }
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Welcome Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          Welcome back, {user?.firstName}!
        </h1>
        <p className="text-gray-600 mt-1">
          Manage your teams, bookings, and vehicles from your dashboard.
        </p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card p-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Users className="text-blue-600" size={20} />
            </div>
            <div>
              <div className="text-2xl font-bold">{teams.length}</div>
              <div className="text-sm text-gray-500">Teams</div>
            </div>
          </div>
        </div>

        <div className="card p-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <Calendar className="text-green-600" size={20} />
            </div>
            <div>
              <div className="text-2xl font-bold">{upcomingBookings.length}</div>
              <div className="text-sm text-gray-500">Upcoming Bookings</div>
            </div>
          </div>
        </div>

        <div className="card p-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <Car className="text-purple-600" size={20} />
            </div>
            <div>
              <div className="text-2xl font-bold">-</div>
              <div className="text-sm text-gray-500">Vehicles</div>
            </div>
          </div>
        </div>

        <div className="card p-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
              <Clock className="text-orange-600" size={20} />
            </div>
            <div>
              <div className="text-2xl font-bold">-</div>
              <div className="text-sm text-gray-500">Track Hours</div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Teams Section */}
        <div className="card">
          <div className="p-4 border-b border-gray-200 flex items-center justify-between">
            <h2 className="font-semibold text-gray-900">Your Teams</h2>
            <Link
              to="/dashboard"
              state={{ createTeam: true }}
              className="btn btn-sm btn-outline"
            >
              <Plus size={16} className="mr-1" />
              New Team
            </Link>
          </div>

          {teams.length === 0 ? (
            <div className="p-8 text-center">
              <Users className="mx-auto text-gray-400 mb-3" size={40} />
              <p className="text-gray-600 mb-4">
                You're not a member of any teams yet.
              </p>
              <Link to="/dashboard" state={{ createTeam: true }} className="btn btn-primary">
                Create Your First Team
              </Link>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {teams.map((team) => (
                <Link
                  key={team.id}
                  to={`/team/${team.slug}`}
                  className="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center font-semibold text-gray-600">
                      {team.name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">{team.name}</div>
                      <div className="text-sm text-gray-500 capitalize">{team.role}</div>
                    </div>
                  </div>
                  <ArrowRight size={18} className="text-gray-400" />
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Upcoming Bookings Section */}
        <div className="card">
          <div className="p-4 border-b border-gray-200 flex items-center justify-between">
            <h2 className="font-semibold text-gray-900">Upcoming Bookings</h2>
            <Link to="/calendar" className="text-sm text-red-600 hover:text-red-700">
              View Calendar
            </Link>
          </div>

          {upcomingBookings.length === 0 ? (
            <div className="p-8 text-center">
              <Calendar className="mx-auto text-gray-400 mb-3" size={40} />
              <p className="text-gray-600 mb-4">
                No upcoming bookings.
              </p>
              <Link to="/book" className="btn btn-primary">
                Book Track Time
              </Link>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {upcomingBookings.map((booking) => (
                <div key={booking.id} className="p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="font-medium text-gray-900">
                        {booking.trackName}
                      </div>
                      <div className="text-sm text-gray-500">
                        {formatDate(booking.bookingDate)} &bull; {booking.startTime} - {booking.endTime}
                      </div>
                      <div className="text-sm text-gray-500">
                        {booking.teamName}
                      </div>
                    </div>
                    <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                      {booking.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="card p-6">
        <h2 className="font-semibold text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Link
            to="/book"
            className="flex flex-col items-center p-4 rounded-lg border border-gray-200 hover:border-red-300 hover:bg-red-50 transition-colors"
          >
            <Calendar className="text-red-600 mb-2" size={24} />
            <span className="text-sm font-medium text-gray-900">Book Track</span>
          </Link>

          <Link
            to="/dashboard/garage"
            className="flex flex-col items-center p-4 rounded-lg border border-gray-200 hover:border-purple-300 hover:bg-purple-50 transition-colors"
          >
            <Car className="text-purple-600 mb-2" size={24} />
            <span className="text-sm font-medium text-gray-900">My Garage</span>
          </Link>

          <Link
            to="/track-rentals"
            className="flex flex-col items-center p-4 rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-colors"
          >
            <Clock className="text-blue-600 mb-2" size={24} />
            <span className="text-sm font-medium text-gray-900">Track Info</span>
          </Link>

          <Link
            to="/calendar"
            className="flex flex-col items-center p-4 rounded-lg border border-gray-200 hover:border-green-300 hover:bg-green-50 transition-colors"
          >
            <Users className="text-green-600 mb-2" size={24} />
            <span className="text-sm font-medium text-gray-900">Events</span>
          </Link>
        </div>
      </div>
    </div>
  );
}
